import React, { useEffect, useRef, useState } from "react";
import { previewContentEmail } from "../../../../services/EmailSerivce";

export interface ServerPreviewProps {
	/** Template id — same value the send pipeline uses as targetType. */
	templateId: number;
	/** Live serialized markup from the builder. */
	content: string;
}

/**
 * Preview rendered by the BACKEND, not by the browser.
 *
 * The edit canvas draws blocks with React, which means every markup block added
 * on the server ([Buttons], [Pin], [Alert], "# heading", "Box: on", ...) shows up
 * here as raw text until someone teaches React to draw it too. That is two
 * renderers for one thing, and they drift — the person building a template ends
 * up unable to trust what they see.
 *
 * So preview asks the backend to render, exactly like the send pipeline does,
 * and shows the result. New server blocks work here the day they ship, with no
 * frontend change.
 */
/**
 * Khổ máy dùng để soi email trong tab "Xem".
 *
 * Trước đây tab này chỉ có MỘT khung, kéo full bề ngang khung chứa — tức bề
 * ngang của hộp thoại, không phải bề ngang thật của máy khách. Hệ quả: lỗi bố
 * cục ở màn hình hẹp không ai thấy lúc soạn, điển hình là hàng hai cột đè chữ
 * ở 375px, lọt tới tận email gửi khách.
 *
 * 375px là iPhone tiêu chuẩn, 600px là bề ngang tối đa của thân email trên máy tính.
 */
const EMAIL_PREVIEW_WIDTHS: { label: string; width: number }[] = [
	{ label: "📱 Điện thoại · 375px", width: 375 },
	{ label: "💻 Máy tính · 600px", width: 600 },
];

export function ServerPreview({ templateId, content }: ServerPreviewProps): JSX.Element {
	const [html, setHtml] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);
	// Guards against an earlier slow request overwriting a newer result.
	const reqIdRef = useRef(0);

	useEffect(() => {
		const myReq = ++reqIdRef.current;
		setLoading(true);
		setError("");

		// Debounced so typing in the editor does not fire a request per keystroke.
		const timer = window.setTimeout(() => {
			previewContentEmail(templateId, content)
				.then((res) => {
					if (reqIdRef.current !== myReq) return;
					setHtml(res.html || "");
					setLoading(false);
				})
				.catch(() => {
					if (reqIdRef.current !== myReq) return;
					setError("Không tải được bản xem thật từ máy chủ. Thử lại sau vài giây.");
					setLoading(false);
				});
		}, 300);

		return () => window.clearTimeout(timer);
	}, [templateId, content]);

	if (error) {
		return (
			<div style={{ padding: 24, color: "#8a2c2c", fontSize: 14 }}>
				{error}
			</div>
		);
	}

	return (
		<div style={{ position: "relative", height: "100%", minHeight: 480 }}>
			{loading && (
				<div
					style={{
						position: "absolute",
						top: 12,
						left: 0,
						right: 0,
						textAlign: "center",
						fontSize: 12,
						color: "#6b7280",
					}}>
					Đang dựng bản xem thật…
				</div>
			)}
			<div
				style={{
					display: "flex",
					gap: 18,
					alignItems: "flex-start",
					justifyContent: "center",
					flexWrap: "wrap",
					height: "100%",
					overflow: "auto",
					opacity: loading ? 0.4 : 1,
					transition: "opacity 120ms",
					background: "#f0f4f8",
					padding: 16,
					boxSizing: "border-box",
				}}>
				{EMAIL_PREVIEW_WIDTHS.map((w) => (
					<div
						key={w.width}
						style={{
							background: "#ffffff",
							border: "1px solid #dbe3f0",
							borderRadius: 12,
							overflow: "hidden",
							flex: "0 0 auto",
						}}>
						<div
							style={{
								font: "600 12px/1 sans-serif",
								color: "#4a5866",
								background: "#f5f7f9",
								borderBottom: "1px solid #dde3e9",
								padding: "8px 10px",
							}}>
							{w.label}
						</div>
						<iframe
							title={w.label}
							srcDoc={html}
							sandbox=""
							style={{
								display: "block",
								width: w.width,
								height: 640,
								border: 0,
								background: "#ffffff",
							}}
						/>
					</div>
				))}
			</div>
		</div>
	);
}
